import { useEffect, useRef } from 'react'

/**
 * THE APP'S STANDARD "BACKGROUND JOB STATUS" PATTERN (DCP-401).
 *
 * A server-side job (a Trigger.dev run, a sync, an import) is started by a
 * mutation that returns long before the job finishes. The UI needs two things
 * from that point on, and this module provides exactly those two, generically:
 *
 * 1. {@link pollWhileActive} — a `refetchInterval` for the query that reads the
 *    job's status. It polls while the SERVER says a job is in flight and stops
 *    the moment it says otherwise. No client-side guessing (baseline
 *    timestamps, grace windows, "did we ever see it syncing" refs): the status
 *    the server stores is the only input.
 * 2. {@link useJobTransitions} — reports each job that finished (active →
 *    inactive, or a moved "last finished run" revision) exactly once, batched
 *    per snapshot, which is where the caller refreshes the data the jobs wrote
 *    (once) and tells the user they finished.
 *
 * The "is it active" predicate is the caller's, and it is where any staleness
 * rule belongs (a status nobody has touched for longer than the job can take is
 * a crashed run, not a running one). Keep it pure and time-aware; both building
 * blocks re-evaluate it on every poll / render.
 *
 * Adopted by the LedgerSync connection syncs (`useBankConnections`,
 * `useLedgerSyncCompletionWatcher`). Bespoke predecessors that should move onto
 * it when next touched: `useSageSyncStatus` (useSageIntegration.ts),
 * `useSyncStatus` (usePlaid.ts) — both still infer "started" from a client-side
 * trigger timestamp and a safety timeout — and the file-import poll
 * (`shouldPollFileImports` in lib/file-import-stall.ts).
 */

/**
 * Structural slice of React Query's `Query` that {@link pollWhileActive} reads.
 * Declared structurally so the factory is assignable to any query's
 * `refetchInterval` without dragging the query's key/error generics along.
 */
interface PolledQuery<TData> {
  state: { data: TData | undefined }
}

/**
 * Build a `refetchInterval` that polls every `intervalMs` while `isActive`
 * holds for the query's current data, and stops polling otherwise.
 *
 * React Query calls it after every fetch, so the decision is re-made on each
 * response: the poll starts on the refetch that first shows an active job and
 * ends on the one that shows none. It also runs only for queries with a
 * MOUNTED observer, so a cached-but-unused key (another sort order of the same
 * list) never polls.
 *
 * @param isActive - Whether the data shows a job in flight; receives
 *   `undefined` before the first response
 * @param intervalMs - Poll period while active
 * @returns A function suitable for `useQuery({ refetchInterval })`
 */
export function pollWhileActive<TData>(
  isActive: (data: TData | undefined) => boolean,
  intervalMs: number,
): (query: PolledQuery<TData>) => number | false {
  return (query) => (isActive(query.state.data) ? intervalMs : false)
}

/** Options for {@link useJobTransitions}. */
export interface UseJobTransitionsOptions<T> {
  /** The latest snapshot of the watched items; `undefined` while loading. */
  items: readonly T[] | undefined
  /** Stable identity of an item across refetches (e.g. its row id). */
  getKey: (item: T) => string
  /** Whether the item's job is in flight right now. May depend on the clock. */
  isActive: (item: T) => boolean
  /**
   * Optional identity of the item's latest FINISHED run (e.g. terminal status
   * + its timestamp), or null while it has none. When an inactive item's
   * revision changes between two snapshots, a whole run started AND finished
   * between them — too fast for the active phase to be observed — and it
   * counts as a finish too.
   */
  getRevision?: (item: T) => string | null
  /**
   * When the snapshot was FETCHED (a query's `dataUpdatedAt`). Two jobs:
   * - Snapshots are evaluated in fetch order only. One OLDER than the last
   *   evaluated snapshot — a cached entry served on a key switch (e.g. a sort
   *   change) — is skipped: it predates what was already seen, and comparing
   *   against it would report a live job as finished.
   * - A refetch that returns identical data (structural sharing keeps the
   *   same `items` reference) still moves this, so a job judged against the
   *   snapshot's age is re-evaluated when a fresh snapshot confirms it.
   */
  snapshotAt?: number
  /**
   * Called ONCE per evaluated snapshot with every item that finished in it
   * (as it is NOW, i.e. its terminal state), and never with an empty list.
   * Batched so a caller refreshing shared caches does it once per snapshot,
   * not once per item.
   */
  onFinish: (finished: T[]) => void
}

/**
 * Detect jobs that just finished between two evaluations of the watched items.
 *
 * An item FINISHED when it is present and inactive now, and on the previous
 * evaluation it was either active, or (with `getRevision`) carried a
 * different non-null revision. Semantics, each one deliberate:
 * - The FIRST snapshot only records a baseline. A job that was already over
 *   when the screen mounted did not "just finish" and must not fire.
 * - An `undefined` snapshot (loading, a query-key switch) is skipped without
 *   touching the baseline, so a sort change that briefly empties the data does
 *   not forget which jobs were running.
 * - A key that DISAPPEARED (row deleted, organization switched) is dropped
 *   silently — there is no item left to report on.
 * - Keyed, not identity-based: a refetch that returns new objects with the
 *   same state produces no transitions, and each transition fires once
 *   because the baseline moves forward in the same pass.
 * - Re-evaluated when `items` OR `snapshotAt` changes, and never for a
 *   snapshot older than the last one evaluated (see `snapshotAt`).
 * - The callbacks are read through a ref, so inline lambdas are fine and never
 *   re-trigger the comparison.
 * - Nothing is scheduled, so there is nothing to cancel on unmount.
 *
 * @param options - See {@link UseJobTransitionsOptions}
 */
export function useJobTransitions<T>({
  items,
  getKey,
  isActive,
  getRevision,
  snapshotAt,
  onFinish,
}: UseJobTransitionsOptions<T>): void {
  // Latest callbacks, so the effect below depends on the inputs alone.
  const callbacks = useRef({ getKey, isActive, getRevision, onFinish })
  callbacks.current = { getKey, isActive, getRevision, onFinish }

  /**
   * Per key, on the previous evaluation: whether it was active and its
   * revision. Null until a first snapshot is seen.
   */
  const previous = useRef<Map<string, { active: boolean; revision: string | null }> | null>(null)
  /** `snapshotAt` of the last evaluated snapshot, for the fetch-order guard. */
  const lastSnapshotAt = useRef(Number.NEGATIVE_INFINITY)

  useEffect(() => {
    if (!items) return
    if (snapshotAt !== undefined) {
      if (snapshotAt < lastSnapshotAt.current) return
      lastSnapshotAt.current = snapshotAt
    }
    const { getKey: keyOf, isActive: active, getRevision: revisionOf, onFinish: finish } =
      callbacks.current

    const next = new Map<string, { active: boolean; revision: string | null }>()
    const finished: T[] = []
    const before = previous.current
    for (const item of items) {
      const key = keyOf(item)
      const state = { active: active(item), revision: revisionOf ? revisionOf(item) : null }
      next.set(key, state)
      const was = before?.get(key)
      if (!was || state.active) continue
      const revisionMoved = state.revision !== null && state.revision !== was.revision
      if (was.active || revisionMoved) finished.push(item)
    }
    // Baseline first, callback second: if the callback synchronously causes a
    // re-render with the same snapshot, it must compare against the new
    // baseline and fire nothing twice.
    previous.current = next
    if (finished.length > 0) finish(finished)
  }, [items, snapshotAt])
}
